import { Schema, SchemaTransformation, Struct } from 'effect';
import { toTicketKeyDecoded } from '../ticket.js';
var PolicyType;
(function (PolicyType) {
    PolicyType["ToS"] = "tos";
    PolicyType["Rules"] = "rules";
})(PolicyType || (PolicyType = {}));
var PolicyRequired;
(function (PolicyRequired) {
    PolicyRequired["Never"] = "never";
    PolicyRequired["Always"] = "always";
    PolicyRequired["Once"] = "once";
    PolicyRequired["OncePerMajor"] = "oncePerMajor";
    PolicyRequired["EveryVersion"] = "everyVersion";
})(PolicyRequired || (PolicyRequired = {}));
var PolicyCoverage;
(function (PolicyCoverage) {
    PolicyCoverage["Coupons"] = "coupons";
    PolicyCoverage["Events"] = "events";
    PolicyCoverage["Items"] = "items";
    PolicyCoverage["PunchCard"] = "punchCard";
})(PolicyCoverage || (PolicyCoverage = {}));
const emptyPolicy = (id) => {
    return {
        id,
        title: 'Ny regel',
        version: '1.0',
        order: 0,
        policyType: PolicyType.Rules,
        acceptanceRequired: PolicyRequired.Never,
        items: [],
        coverage: [],
        content: '',
        contentType: 'base64'
    };
};
const toPolicies = (raw) => {
    if (!Array.isArray(raw))
        return [];
    return raw.map((p) => toPolicy(p));
};
const toPolicy = (data) => {
    return {
        id: data.id,
        title: data.title,
        version: data.version,
        order: data.order,
        policyType: data.policyType,
        acceptanceRequired: data.acceptanceRequired,
        items: data.items || data.appliesToItems || [],
        coverage: data.coverage || [PolicyCoverage.Items],
        content: data.content,
        contentType: data.contentType
    };
};
/**
 * One entry of `siteData.policies` as stored, for the site document's stored
 * schema (site.ts). The defaults are the ones `toPolicy` applies: `items` was
 * once called `appliesToItems`, and a policy saved before `coverage` existed
 * covers items. `content` is optional because the policy text has moved to
 * the pages collection and the mirrored copy here is on its way out.
 */
const StoredPolicyFields = Schema.Struct({
    id: Schema.String,
    title: Schema.String,
    order: Schema.Number,
    version: Schema.String,
    policyType: Schema.Enum(PolicyType),
    acceptanceRequired: Schema.Enum(PolicyRequired),
    items: Schema.optional(Schema.Array(Schema.String)),
    appliesToItems: Schema.optional(Schema.Array(Schema.String)),
    coverage: Schema.optional(Schema.Array(Schema.Enum(PolicyCoverage))),
    content: Schema.optional(Schema.String),
    contentType: Schema.optional(Schema.Literals(['base64', 'builtin']))
});
const PolicyFields = Schema.Struct({
    id: Schema.String,
    title: Schema.String,
    order: Schema.Number,
    version: Schema.String,
    policyType: Schema.Enum(PolicyType),
    acceptanceRequired: Schema.Enum(PolicyRequired),
    items: Schema.mutable(Schema.Array(Schema.String)),
    coverage: Schema.mutable(Schema.Array(Schema.Enum(PolicyCoverage))),
    content: Schema.optional(Schema.String),
    contentType: Schema.optional(Schema.Literals(['base64', 'builtin']))
}).mapFields(Struct.map(Schema.mutableKey));
const PolicySchema = StoredPolicyFields.pipe(Schema.decodeTo(Schema.toType(PolicyFields), SchemaTransformation.transform({
    decode: ({ appliesToItems, ...policy }) => ({
        ...policy,
        items: [...(policy.items ?? appliesToItems ?? [])],
        coverage: [...(policy.coverage ?? [PolicyCoverage.Items])]
    }),
    encode: (policy) => policy
})));
const policyRequiredForItemTicketKey = (policy, ticketKey) => {
    if (!policy.coverage.includes(PolicyCoverage.Items))
        return false;
    if (policy.items.length === 0)
        return false;
    const ticketDecoded = toTicketKeyDecoded(ticketKey);
    return policy.items.includes(ticketDecoded.spaceId);
};
const policyRequiredForCart = (cart, policy) => {
    if (policy.acceptanceRequired === PolicyRequired.Never)
        return false;
    // Applies to items
    const ticketThatApplies = Object.values(cart.tickets).find((ticket) => policyRequiredForItemTicketKey(policy, ticket.ticketKey));
    if (ticketThatApplies)
        return true;
    // Applies to coupons.
    //
    // `couponsToBuy` is a Record<couponKey, number>, not an array — an index
    // signature of `number` also types `.length`, so `cart.couponsToBuy.length`
    // compiled cleanly and evaluated to `undefined > 0`, i.e. always false. This
    // branch could therefore never fire: a policy configured for "Gavekort" in
    // the policy editor was silently never required.
    const appliesToCoupons = Object.keys(cart.couponsToBuy).length > 0 &&
        policy.coverage.includes(PolicyCoverage.Coupons);
    if (appliesToCoupons) {
        return true;
    }
    // Applies to events. PolicyCoverage.Events is offered by the policy editor
    // ("Arrangementer") and had no branch here at all, so it too could never be
    // required.
    const appliesToEvents = cart.events.length > 0 && policy.coverage.includes(PolicyCoverage.Events);
    if (appliesToEvents) {
        return true;
    }
    // Applies to punch cards
    return (cart.punchCardsToBuy.length > 0 &&
        policy.coverage.includes(PolicyCoverage.PunchCard));
};
const policiesRequiredForCart = (cart, policies) => {
    return policies.filter((policy) => policyRequiredForCart(cart, policy));
};
const policiesRequiredForUser = (cart, policies, user) => {
    return policiesRequiredForCart(cart, policies).filter((policy) => !policyAccepted(policy, user));
};
const policiesRequired = (cart, siteData, user) => {
    return policiesRequiredForUser(cart, siteData.policies, user).filter((p) => siteData.pages.rules.enabled || p.policyType === PolicyType.ToS);
};
const policyAccepted = (policy, siteUser) => {
    if (policy.acceptanceRequired === PolicyRequired.Never)
        return true;
    if (policy.acceptanceRequired === PolicyRequired.Always)
        return false;
    if (policy.acceptanceRequired === PolicyRequired.Once) {
        return !!siteUser.policy[policy.id];
    }
    const userVersion = siteUser.policy[policy.id]?.version ?? '';
    const userMajor = userVersion.split('.')[0];
    const policyMajor = policy.version.split('.')[0];
    if (policy.acceptanceRequired === PolicyRequired.OncePerMajor) {
        return userMajor === policyMajor;
    }
    if (policy.acceptanceRequired === PolicyRequired.EveryVersion) {
        return userVersion === policy.version;
    }
    return false;
};
const generateToS = (info) => {
    return ` 
Betingelser
===========
## Partene 
Selger er ${info.ownerNameLegal}, ${info.addressLine1}, ${info.addressLine2}, ${info.email}, ${info.phone}, Org. ${info.orgNo} 
og betegnes i det følgende som selger/selgeren.
Kjøper er den forbrukeren som foretar bestillingen, og betegnes i det følgende som kjøper/kjøperen.

## Pris
Den oppgitte prisen for varen og tjenester er den totale prisen kjøper skal betale.
Denne prisen inkluderer alle avgifter og tilleggskostnader. Ytterligere kostnader som selger
før kjøpet ikke har informert om, skal kjøper ikke bære.

## Avtaleinngåelse
Avtalen er bindende for begge parter når kjøperen har sendt sin bestilling til selgeren.

Avtalen er likevel ikke bindende hvis det har forekommet skrive- eller tastefeil i tilbudet
fra selgeren i bestillingsløsningen i nettbutikken eller i kjøperens bestilling, og den annen
part innså eller burde ha innsett at det forelå en slik feil.

## Betalingen
Selgeren kan kreve betaling for varen fra det tidspunkt den blir bestilt kjøperen.

Dersom kjøperen bruker kredittkort eller debetkort ved betaling, kan selgeren reservere kjøpesummen
på kortet ved bestilling. For korttidsleie blir kortet belastet omgående. For langtidsleie blir
 kortet blir belastet i god tid før leien finner sted.

Ved betaling med faktura, blir fakturaen til kjøperen utstedt ved forsendelse av varen.
Betalingsfristen fremgår av fakturaen og er på minimum 14 dager fra mottak.

Kjøpere under 18 år kan ikke betale med etterfølgende faktura.

## Levering
Levering er skjedd når kjøperen, eller hans representant, har overtatt tingen. For leide timer så
er dette ved betalingstidspunktet (man har overtatt retten til å bruke hallen).

Med mindre det er eksplisitt angitt i de spesielle vilkårene for det konkrete kjøpet og/eller tjenesten,
er det ingen bytterett eller refusjonsadgang for produkter med en angitt frist for bruk (dato eller periode).

Se også punkt 7 vedrørende angrerett.

## Risikoen for varen
Risikoen for varen går over på kjøper når han, eller hans representant,
har fått varene levert i tråd med punkt 5.

## Angrerett
Med mindre avtalen er unntatt fra angrerett, kan kjøperen angre kjøpet av varen i henhold til
angrerettloven. Leide timer til fritidsformål er unntatt fra angrerett jevnfør Angrerettloven § 22 bokstav m.

For andre kjøp enn av leide timer må kjøperen gi selger melding om bruk av angreretten innen 14 dager fra
fristen begynner å løpe. I fristen inkluderes alle kalenderdager. Dersom fristen ender på en lørdag,
helligdag eller høytidsdag forlenges fristen til nærmeste virkedag.

Angrefristen anses overholdt dersom melding er sendt før utløpet av fristen. Kjøper har bevisbyrden for
at angreretten er blitt gjort gjeldende, og meldingen bør derfor skje skriftlig
(angrerettskjema, e-post eller brev).

## Forsinkelse og manglende levering - kjøpernes rettigheter og frist for å melde krav
Dersom selgeren ikke leverer varen eller leverer den for sent i henhold til avtalen mellom partene,
og dette ikke skyldes kjøperen eller forhold på kjøperens side, kan kjøperen i henhold til reglene
i forbrukerkjøpslovens kapittel 5 etter omstendighetene kreve oppfyllelse,
heve avtalen og/eller kreve erstatning fra selgeren.

Ved krav om misligholdsbeføyelser bør meldingen av bevishensyn være skriftlig (for eksempel e-post).

### Oppfyllelse

Kjøper kan fastholde kjøpet og kreve oppfyllelse fra selger. Kjøper kan imidlertid ikke kreve oppfyllelse
dersom det foreligger en hindring som selgeren ikke kan overvinne, eller dersom oppfyllelse vil medføre
en så stor ulempe eller kostnad for selger at det står i vesentlig misforhold til kjøperens interesse
at selgeren oppfyller. Skulle vanskene falle bort innen rimelig tid, kan kjøper likevel kreve oppfyllelse.

Kjøperen taper sin rett til å kreve oppfyllelse om han eller hun venter urimelig lenge med å fremme kravet.
 
### Heving

Dersom selgeren ikke leverer varen på leveringstidspunktet, skal kjøperen oppfordre selger til å levere
innen en rimelig tilleggsfrist for oppfyllelse. Dersom selger ikke leverer varen innen tilleggsfristen,
kan kjøperen heve kjøpet.

Kjøper kan imidlertid heve kjøpet umiddelbart hvis selger nekter å levere varen. Tilsvarende gjelder dersom
levering til avtalt tid var avgjørende for inngåelsen av avtalen, eller dersom kjøperen har underrettet
selger om at leveringstidspunktet er avgjørende.

Leveres tingen etter tilleggsfristen forbrukeren har satt eller etter leveringstidspunktet som var
avgjørende for inngåelsen av avtalen, må krav om heving gjøres gjeldende innen rimelig tid etter at
kjøperen fikk vite om leveringen.

### Erstatning

Kjøperen kan kreve erstatning for lidt tap som følge av forsinkelsen. Dette gjelder imidlertid ikke dersom
selgeren godtgjør at forsinkelsen skyldes hindring utenfor selgers kontroll som ikke med rimelighet kunne
blitt tatt i betraktning på avtaletiden, unngått, eller overvunnet følgene av.

## Mangel ved varen - kjøperens rettigheter og reklamasjonsfrist
Hvis det foreligger en mangel ved varen må kjøper innen rimelig tid etter at den ble oppdaget eller burde
ha blitt oppdaget, gi selger melding om at han eller hun vil påberope seg mangelen. Kjøper har alltid
reklamert tidsnok dersom det skjer innen 2 mnd. fra mangelen ble oppdaget eller burde blitt oppdaget.
Reklamasjon kan skje senest to år etter at kjøper overtok varen. Dersom varen eller deler av den er ment
å vare vesentlig lenger enn to år, er reklamasjonsfristen fem år.
  
Dersom varen har en mangel og dette ikke skyldes kjøperen eller forhold på kjøperens side, kan kjøperen i
henhold til reglene i forbrukerkjøpsloven kapittel 6 etter omstendighetene holde kjøpesummen tilbake,
velge mellom retting og omlevering, kreve prisavslag, kreve avtalen hevet og/eller kreve erstatning
fra selgeren.
  
Reklamasjon til selgeren bør skje skriftlig.
  
### Retting eller omlevering
  
Kjøperen kan velge mellom å kreve mangelen rettet eller levering av tilsvarende ting. Selger kan likevel
motsette seg kjøperens krav dersom gjennomføringen av kravet er umulig eller volder selgeren urimelige
kostnader. Retting eller omlevering skal foretas innen rimelig tid. Selger har i utgangspunktet ikke rett
til å foreta mer enn to avhjelpsforsøk for samme mangel.
  
### Prisavslag
  
Kjøper kan kreve et passende prisavslag dersom varen ikke blir rettet eller omlevert. Dette innebærer at
forholdet mellom nedsatt og avtalt pris svarer til forholdet mellom tingens verdi i mangelfull og
kontraktsmessig stand. Dersom særlige grunner taler for det, kan prisavslaget i stedet settes lik
mangelens betydning for kjøperen.
  
### Heving
  
Dersom varen ikke er rettet eller omlevert, kan kjøperen også heve kjøpet når mangelen ikke er uvesentlig.

## Selgerens rettigheter ved kjøperens mislighold
Dersom kjøperen ikke betaler eller oppfyller de øvrige pliktene etter avtalen eller loven, og dette
ikke skyldes selgeren eller forhold på selgerens side, kan selgeren i henhold til reglene i
forbrukerkjøpsloven kapittel 9 etter omstendighetene holde varen tilbake, kreve oppfyllelse av avtalen,
kreve avtalen hevet samt kreve erstatning fra kjøperen. Selgeren vil også etter omstendighetene kunne kreve
renter ved forsinket betaling, inkassogebyr og et rimelig gebyr ved uavhentede varer.
  
### Oppfyllelse
  
Selger kan fastholde kjøpet og kreve at kjøperen betaler kjøpesummen. Er varen ikke levert, taper selgeren
sin rett dersom han venter urimelig lenge med å fremme kravet.
  
### Heving
  
Selger kan heve avtalen dersom det foreligger vesentlig betalingsmislighold eller annet vesentlig mislighold
fra kjøperens side. Selger kan likevel ikke heve dersom hele kjøpesummen er betalt. Fastsetter selger en
rimelig tilleggsfrist for oppfyllelse og kjøperen ikke betaler innen denne fristen, kan selger heve kjøpet.
  
### Renter ved forsinket betaling/inkassogebyr
  
Dersom kjøperen ikke betaler kjøpesummen i henhold til avtalen, kan selger kreve renter av kjøpesummen
etter forsinkelsesrenteloven. Ved manglende betaling kan kravet, etter forutgående varsel, bli sendt
til Kjøper kan da bli holdt ansvarlig for gebyr etter inkassoloven.
  
### Gebyr ved uavhentede ikke-forskuddsbetalte varer
  
Dersom kjøperen unnlater å hente ubetalte varer, kan selger belaste kjøper med et gebyr.
Gebyret skal maksimalt dekke selgerens faktiske  utlegg for å levere varen til kjøperen.
Et slikt gebyr kan ikke belastes kjøpere under 18  år.

## Personopplysninger
Behandlingsansvarlig for innsamlede personopplysninger er selger. Med mindre kjøperen samtykker til
noe annet, kan selgeren, i tråd med personopplysningsloven, kun innhente og lagre de personopplysninger
som er nødvendig for at selgeren skal kunne gjennomføre forpliktelsene etter avtalen.
Kjøperens personopplysninger vil kun bli utlevert til andre hvis det er nødvendig for at selger skal
få gjennomført avtalen med kjøperen, eller i lovbestemte tilfelle.

## Konfliktløsning
Klager rettes til selger innen rimelig tid, jf. punkt 8 og 9. Partene skal forsøke å løse eventuelle
tvister i minnelighet. Dersom dette ikke lykkes, kan kjøperen ta kontakt med Forbrukerrådet for mekling.
Forbrukerrådet er tilgjengelig på telefon 23  400  500  eller www.forbrukerradet.no.
  
Europa-Kommisjonens klageportal kan også brukes hvis du ønsker å inngi en klage. Det er særlig relevant,
hvis du er forbruker bosatt i et annet EU-land. Klagen inngis her: http://ec.europa.eu/odr.`;
};
export { PolicyType, PolicyRequired, PolicyCoverage, PolicySchema, toPolicy, toPolicies, generateToS, emptyPolicy, policiesRequiredForUser, policiesRequiredForCart, policyRequiredForCart, policiesRequired, policyRequiredForItemTicketKey, policyAccepted };
//# sourceMappingURL=policy.js.map